import React, { useId, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import DurationBadge from './DurationBadge';

/** Splits plain transcript text into paragraphs on blank lines. */
export function toParagraphs(text) {
  return String(text)
    .split(/\n\s*\n/)
    .map((part) => part.trim())
    .filter(Boolean);
}

/**
 * "Read the transcript instead" row. The whole header is one real button
 * with aria-expanded; the read-time badge sits inside it. Starts collapsed;
 * remount it (key on the video) to collapse it again.
 */
TranscriptDisclosure.propTypes = {
  transcript: PropTypes.string.isRequired,
  readTimeMinutes: PropTypes.number
};

export default function TranscriptDisclosure(props) {
  const { transcript, readTimeMinutes } = props;
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const panelId = useId();

  const readTime = readTimeMinutes
    ? t('home_videos.dialog.read_time', { count: readTimeMinutes })
    : null;

  return (
    <div className="w-full bg-white rounded-[10px]">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((value) => !value)}
        className="group w-full min-h-[54px] flex items-center justify-between gap-4 px-5 py-2.5 text-left rounded-[10px] focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2"
      >
        <span className="flex flex-wrap items-center gap-x-4 gap-y-2 min-w-0">
          <span className="font-semibold text-[17px] leading-6 text-cs-gray-900">
            {t('home_videos.dialog.transcript')}
          </span>
          <DurationBadge label={readTime} variant="solid" />
        </span>
        <span className="flex-shrink-0 inline-flex items-center gap-1 text-[17px] leading-6 text-cs-blue-600 group-hover:underline">
          {open ? t('home_videos.dialog.hide') : t('home_videos.dialog.view')}
          <ChevronDownIcon
            className={`size-5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
            strokeWidth={2}
            aria-hidden="true"
          />
        </span>
      </button>

      <div
        id={panelId}
        hidden={!open}
        className="px-5 pb-5 font-normal text-[15px] leading-normal text-cs-gray-700 flex flex-col gap-3"
      >
        {open && toParagraphs(transcript).map((paragraph, index) => (
          <p key={index} className="!mb-0 whitespace-pre-line">{paragraph}</p>
        ))}
      </div>
    </div>
  );
}
